import React, { MutableRefObject, useEffect, useMemo, useState } from "react";
import "alpinejs";
import { config } from "@app/config";
import { SearchspringRecommendProvider } from "@app/providers/searchspring";
import {
  SearchspringRecommendItem,
  useSearchspringRecommendResults,
} from "@app/hooks/use-searchspring-recommend-results";
import { SearchspringRecommendAttributes } from "@app/hooks/use-searchspring-upsell";
import { useSearchspringProductSearch } from "@app/hooks/use-searchspring-product-search";
import Alpine from "alpinejs";
import { ACTION_MAP } from "@app/lib/actions";
import { CART_EVENTS, GOOGLE_ANALYTIC_EVENTS, INTERACTION_EVENTS, INTERACTION_TYPES } from "@app/lib/events";

const viewItemEvent = () => {};

type CartUpsellLocale = {
  heading: string;
  desktop_button_label: string;
  mobile_button_label: string;
  size_label: string;
  color_label: string;
};

export type Props = {
  config: typeof config;
  upsell: {
    profile_id: string;
    locale: CartUpsellLocale;
  };
};

const features_render = {
  colors: true,
};

/**
 * Parses the JSON results from SearchingSpring.
 */
const parseSearchSpringJson = (stringJson: string) =>
  JSON.parse(((stringJson as string) ?? "[]")?.split("&quot;")?.join('"'));

/**
 * Parses the results data
 * @param type
 * @param data
 * @returns
 */
const formatResult = (
  type: "recommend" | "search",
  data: any,
): SearchspringRecommendItem<SearchspringRecommendAttributes> | null => {
  if (type === "search") {
    const _data = data;
    if (!_data) return null;
    return {
      id: _data.uid,
      attributes: {
        variants: parseSearchSpringJson(_data?.variants as string),
        mfield_dotapparel21_siblings: parseSearchSpringJson(_data?.mfield_dotapparel21_siblings as string),
      },
      brand: _data?.brand,
      msrp: _data.msrp,
      name: _data.name,
      price: _data.price,
      sku: _data.sku,
      uid: _data.uid,
      thumbnailImageUrl: _data.imageUrl,
      imageUrl: _data.imageUrl,
      url: _data.url,
    };
  }

  return data as SearchspringRecommendItem<SearchspringRecommendAttributes>;
};

function UpsellResult({ locale }: { locale: CartUpsellLocale }) {
  const [product, setProduct] = useState<SearchspringRecommendItem<SearchspringRecommendAttributes>>();
  const results = useSearchspringRecommendResults<SearchspringRecommendAttributes>();
  const productSearch = useSearchspringProductSearch();

  const handleColorSwap = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const handle = event.currentTarget.value;
    const interaction = product?.attributes.mfield_dotapparel21_siblings.find((sibling) => sibling.handle === handle);
    if (interaction) {
      /** Google Analytics */
      document.dispatchEvent(
        new CustomEvent(INTERACTION_EVENTS.INTERACTION_SELECT, {
          detail: {
            event: GOOGLE_ANALYTIC_EVENTS.INTERACTION_EVENT_CLICK,
            eventAction: INTERACTION_TYPES.SELECT,
            eventModuleName: "click recommendations",
            eventActionText: interaction?.title,
            eventActionIdentifier: handle,
          },
        }),
      );
    }

    productSearch.updateHandle(handle);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variantId = parseInt((e.currentTarget.elements.namedItem("size") as HTMLInputElement).value ?? "");
    if (!variantId) return;
    const variant = product?.attributes.variants.find((variant) => variant.id === variantId.toString());
    const membership_price = variant?.mfield_pricing_members ?? 0;

    document.dispatchEvent(
      new CustomEvent(INTERACTION_EVENTS.INTERACTION_SELECT, {
        detail: {
          event: GOOGLE_ANALYTIC_EVENTS.INTERACTION_EVENT_CLICK,
          eventAction: INTERACTION_TYPES.ADD_TO_CART,
          eventModuleName: "add to cart recommendations",
          eventActionText: "Add to cart",
          eventActionIdentifier: product?.url,
        },
      }),
    );

    const response = await ACTION_MAP[CART_EVENTS.ADD]({ variantId, membership_price });
  };

  /** Initial state load getting the product from profile */
  useEffect(() => {
    if (!results.length) return;

    const firstAvailableProduct = results.find((result) => {
      const product = formatResult("recommend", result);
      const isGiftCard = product?.attributes?.variants.some((variant) => variant.sku.includes("90236"));
      const hasAvailableVariants = product?.attributes?.variants.some(
        (variant) => variant.inventory_quantity > 0 || variant.tracked === false,
      );

      return hasAvailableVariants && !isGiftCard;
    });

    // Don't set product if no variants are available
    if (!firstAvailableProduct) return;

    document.dispatchEvent(
      new CustomEvent(INTERACTION_EVENTS.INTERACTION_IMPRESSION, {
        detail: {
          event: GOOGLE_ANALYTIC_EVENTS.INTERACTION_EVENT_IMPRESSION,
          eventAction: INTERACTION_TYPES.IMPRESSION,
          eventModuleName: `cart ${locale.heading}`,
          eventActionText: firstAvailableProduct.name,
          eventActionIdentifier: locale.heading,
        },
      }),
    );
    setProduct(firstAvailableProduct);
  }, [results]);

  /** Getting product data when the color attribute changes  */
  useEffect(() => {
    const product = formatResult("search", productSearch.currentResult);
    if (!product) return;
    setProduct(product);
  }, [productSearch.currentResult]);

  const isSoldOut = useMemo(() => {
    if (!product) return false;
    return product.attributes.variants.every((variant) => variant.inventory_quantity === 0 && variant.tracked);
  }, [product]);

  if (!product) return null;

  const firstImage = product?.attributes?.variants[0]?.image ?? "";

  return (
    <form
      onSubmit={handleFormSubmit}
      className="flex min-h-24 w-full flex-col gap-4 rounded-lg bg-neutrals-grey-5 px-4 py-4 md:pr-8 lg:block"
    >
      <div className="flex h-full flex-row gap-4">
        <a href={product.url} className="hidden aspect-square w-36 overflow-hidden rounded-lg bg-slate-600 lg:block">
          <img src={firstImage} className="h-full w-full object-cover" />
        </a>
        <div className="flex w-full flex-col justify-between gap-4">
          <div className="flex flex-row gap-4 lg:gap-0">
            <a href={product.url} className="aspect-square w-24 overflow-hidden rounded-lg bg-slate-600 lg:hidden">
              <img src={firstImage} className="h-full w-full object-cover" />
            </a>
            <div className="flex w-full flex-col gap-1">
              <div className="text-label text-neutrals-grey-2">{locale.heading}</div>
              <a href={product.url} className="text-body-md font-bold">
                {product.name}
              </a>
              <div className="text-body-md">${product.price}</div>
            </div>
            <div className="m-auto hidden flex-grow lg:flex">
              <button className="btn btn-md btn-outline-light w-max">{locale.desktop_button_label}</button>
            </div>
          </div>
          <div className="flex flex-row gap-2 md:gap-4">
            <div className="field field-horizontal-select group rounded-md !bg-neutrals-white max-md:grow md:max-w-max">
              <label htmlFor="size" className="field-label">
                {locale.size_label}
              </label>
              <select
                disabled={product.attributes.variants?.length <= 1}
                name="size"
                className="text-body-sm h-fit bg-transparent focus:outline-none"
              >
                {!isSoldOut ? (
                  product.attributes.variants.map((variant) => {
                    return (
                      <option value={variant.id} disabled={variant.inventory_quantity === 0 && variant.tracked}>
                        {variant.option2}
                      </option>
                    );
                  })
                ) : (
                  <option selected disabled>
                    Sold Out
                  </option>
                )}
              </select>
            </div>
            <div className="field field-horizontal-select group rounded-md !bg-neutrals-white max-md:grow md:max-w-max">
              <label className="field-label">{locale.color_label}</label>
              <select
                onChange={handleColorSwap}
                disabled={product.attributes.mfield_dotapparel21_siblings?.length <= 1}
                className="text-body-sm h-fit w-full bg-transparent focus:outline-none"
              >
                {product.attributes.mfield_dotapparel21_siblings?.map((variant) => {
                  if (!variant.published) return null;
                  return <option value={variant.handle}>{variant.colourName}</option>;
                })}
              </select>
            </div>
            <button className="btn btn-md btn-outline-light shrink-0 justify-center py-2 md:hidden">
              {locale.mobile_button_label}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export function CartUpsell({ upsell }: Props) {
  return (
    <SearchspringRecommendProvider tags={upsell.profile_id} category={"products"} limit="20">
      <UpsellResult locale={upsell.locale} />
    </SearchspringRecommendProvider>
  );
}
